import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchWeatherDetails } from 'utils/methods';

const LocationContext = createContext();

export const useLocationStore = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationStore must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [locationProfiles, setLocationProfiles] = useState(() => {
    const saved = localStorage.getItem('locationProfiles');
    return saved ? JSON.parse(saved) : [];
  });

  const [selectedLocationProfile, setSelectedLocationProfile] = useState(() => {
    const saved = localStorage.getItem('selectedLocationProfile');
    return saved ? JSON.parse(saved) : null;
  });

  // Listen for localStorage changes (including from other components)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedLocationProfile') {
        const newProfile = e.newValue ? JSON.parse(e.newValue) : null;
        setSelectedLocationProfile(newProfile);
      } else if (e.key === 'locationProfiles') {
        const newProfiles = e.newValue ? JSON.parse(e.newValue) : [];
        setLocationProfiles(newProfiles);
      }
    };

    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);

    // Also set up a custom event listener for same-tab updates
    const handleCustomLocationChange = (e) => {
      if (e.detail.key === 'selectedLocationProfile') {
        setSelectedLocationProfile(e.detail.value);
      } else if (e.detail.key === 'locationProfiles') {
        setLocationProfiles(e.detail.value);
      }
    };

    window.addEventListener('localStorageUpdate', handleCustomLocationChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleCustomLocationChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('locationProfiles', JSON.stringify(locationProfiles));
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('localStorageUpdate', {
      detail: { key: 'locationProfiles', value: locationProfiles }
    }));
  }, [locationProfiles]);

  useEffect(() => {
    localStorage.setItem('selectedLocationProfile', JSON.stringify(selectedLocationProfile));
    // Dispatch custom event for same-tab updates  
    window.dispatchEvent(new CustomEvent('localStorageUpdate', {
      detail: { key: 'selectedLocationProfile', value: selectedLocationProfile }
    }));
  }, [selectedLocationProfile]);

  const addProfile = (profile) => {
    const newProfile = { ...profile, id: Date.now() };
    setLocationProfiles(prev => [...prev, newProfile]);
    return newProfile;
  };

  const updateProfile = (updatedProfile) => {
    setLocationProfiles(prev => 
      prev.map(profile => profile.id === updatedProfile.id ? { ...profile, ...updatedProfile } : profile)
    );
    
    // If the updated profile is the currently selected one, update the selected profile too
    if (selectedLocationProfile?.id === updatedProfile.id) {
      setSelectedLocationProfile(updatedProfile);
      // Force immediate update for same-tab
      window.dispatchEvent(new CustomEvent('localStorageUpdate', {
        detail: { key: 'selectedLocationProfile', value: updatedProfile }
      }));
    }
  };

  const deleteProfile = (id) => {
    setLocationProfiles(prev => prev.filter(profile => profile.id !== id));
    if (selectedLocationProfile?.id === id) {
      setSelectedLocationProfile(null);
    }
  };

  const setSelectedLocation = (profile) => {
    setSelectedLocationProfile(profile);
    // Force immediate update for same-tab
    window.dispatchEvent(new CustomEvent('localStorageUpdate', {
      detail: { key: 'selectedLocationProfile', value: profile }
    }));
  };

  const getSelectedLocation = () => {
    return selectedLocationProfile;
  };

  const fetchCurrentLocationData = async () => {
    try {
      if (navigator.geolocation) {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });

        const { latitude, longitude } = position.coords;
        const weatherData = await fetchWeatherDetails({ latitude, longitude });

        const currentLocationProfile = {
          id: 1,
          name: 'Current Location',
          location: `${weatherData.location.name}, ${weatherData.location.country}`,
          temperature: Math.round(weatherData.current.temp_c),
          humidity: weatherData.current.humidity,
          editable: false
        };

        // Add or update the current location profile in locationProfiles
        setLocationProfiles(prev => {
          const newProfiles = [...prev];
          const index = newProfiles.findIndex(p => p.id === 1);
          if (index !== -1) {
            newProfiles[index] = currentLocationProfile;
          } else {
            newProfiles.unshift(currentLocationProfile); // Add to the beginning
          }
          return newProfiles;
        });

        setSelectedLocationProfile(currentLocationProfile);
        return currentLocationProfile;
      }
    } catch (error) {
      console.error('Error fetching current location data:', error);
      throw error;
    }
  };

  const initializeDefaultProfile = async () => {
    try {
      const currentLocationProfile = await fetchCurrentLocationData();
      if (currentLocationProfile) {
        setLocationProfiles([currentLocationProfile]); // Ensure only the current location is set initially
        setSelectedLocation(currentLocationProfile); // Set the current location as the selected profile
      }
    } catch (error) {
      console.error('Failed to initialize default profile:', error);
    }
  };

  const updateCurrentLocationProfile = async () => {
    try {
      const currentLocationProfile = await fetchCurrentLocationData();
      if (currentLocationProfile) {
        setLocationProfiles(prev => {
          const newProfiles = [...prev];
          const index = newProfiles.findIndex(p => p.id === 1);
          if (index !== -1) {
            newProfiles[index] = currentLocationProfile;
          }
          return newProfiles;
        });
      }
    } catch (error) {
      console.error('Failed to update current location profile:', error);
    }
  };

  const value = {
    locationProfiles,
    selectedLocationProfile,
    setSelectedLocation,
    getSelectedLocation,
    addProfile,
    updateProfile,
    deleteProfile,
    fetchCurrentLocationData,
    initializeDefaultProfile,
    updateCurrentLocationProfile
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
